<template>
  <div class="pb-1">
    <b-card class="mb-2">
      <b-row>
        <b-col md="5">
          <e-store-combo
            id="auto-delivery-monitor_stores"
            v-model="storeIds"
            :multiple="true"
            :required="false"
            :select-single-option="false"
            :label="$t('Lojas')"
            :placeholder="$t('Todas as lojas')"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="auto-delivery-monitor_period-preset"
            v-model="periodPreset"
            type="vue-select"
            :label="$t('Período')"
            :clearable="false"
            :options="periodOptions"
          />
        </b-col>
        <b-col
          v-if="periodPreset === periodPresets.CUSTOM"
          md="4"
        >
          <FormulateInput
            id="auto-delivery-monitor_period-range"
            v-model="rangeDate"
            type="date-range-picker"
            :label="$t('Datas')"
          />
        </b-col>
      </b-row>
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <small class="text-muted">
          {{ lastUpdatedLabel }}
          <span v-if="autoRefreshPaused"> · {{ $t('atualização automática pausada (aba em segundo plano)') }}</span>
        </small>
        <e-button
          id="auto-delivery-monitor_refresh"
          icon="arrow-clockwise"
          :busy="fetching"
          :text="$t('Atualizar')"
          @click="refresh"
        />
      </div>
    </b-card>

    <monitor-health
      :health="health"
      :selected-alert="alertFilter && alertFilter.alert"
      @select="selectAlert"
    />

    <b-alert
      :show="!!(overview && overview.truncated)"
      variant="warning"
    >
      {{ $t('O período tem mais vendas do que o painel exibe. Mostrando as mais recentes:') }}
      {{ overview && overview.maxSales }}.
      {{ $t('Restrinja o período ou as lojas.') }}
    </b-alert>

    <monitor-summary :summary="summary" />

    <b-card no-body>
      <b-tabs
        v-model="activeTab"
        card
      >
        <b-tab :title="`${$t('Vendas')} (${sales.length})`">
          <monitor-sales-table
            :sales="sales"
            :alert-filter="alertFilter"
            @open="openSale"
            @clear-alert="alertFilter = null"
          />
        </b-tab>
        <b-tab :title="`${$t('Por loja')} (${stores.length})`">
          <monitor-stores-table
            :stores="stores"
            @select-store="selectStore"
          />
        </b-tab>
      </b-tabs>
    </b-card>

    <monitor-sale-sidebar
      :sale-id="openedSaleId"
      @hidden="openedSaleId = null"
    />
  </div>
</template>

<script>
import { BAlert, BCard, BCol, BRow, BTab, BTabs } from 'bootstrap-vue'
import moment from 'moment'
import { mapActions, mapGetters, mapState } from 'vuex'
import { EButton, EStoreCombo } from '@/views/components'
import { periodPresets } from '@/store/pages/sale/auto-delivery-monitor'
import MonitorHealth from './components/MonitorHealth.vue'
import MonitorSaleSidebar from './components/MonitorSaleSidebar.vue'
import MonitorSalesTable from './components/MonitorSalesTable.vue'
import MonitorStoresTable from './components/MonitorStoresTable.vue'
import MonitorSummary from './components/MonitorSummary.vue'

const AUTO_REFRESH_MS = 30000
const MAX_PERIOD_DAYS = 7

export default {
  name: 'AutoDeliveryMonitor',

  components: {
    BAlert,
    BCard,
    BCol,
    BRow,
    BTab,
    BTabs,
    EButton,
    EStoreCombo,
    MonitorHealth,
    MonitorSaleSidebar,
    MonitorSalesTable,
    MonitorStoresTable,
    MonitorSummary,
  },

  data() {
    return {
      periodPresets,
      activeTab: 0,
      alertFilter: null,
      openedSaleId: null,
      refreshTimer: null,
      autoRefreshPaused: false,
      now: Date.now(),
    }
  },

  computed: {
    ...mapState('pages/sale/autoDeliveryMonitor', [
      'filters',
      'overview',
      'health',
      'fetching',
      'lastUpdatedAt',
    ]),
    ...mapGetters('pages/sale/autoDeliveryMonitor', ['sales', 'stores', 'summary']),
    storeIds: {
      get() {
        return this.filters.storeIds
      },
      set(storeIds) {
        this.setFilters({ storeIds: storeIds || [] })
        this.refresh()
      },
    },
    periodPreset: {
      get() {
        return this.filters.periodPreset
      },
      set(periodPreset) {
        this.setFilters({ periodPreset })
        this.refresh()
      },
    },
    rangeDate: {
      get() {
        return this.filters.rangeDate
      },
      set(rangeDate) {
        this.setFilters({ rangeDate })
        this.refresh()
      },
    },
    periodOptions() {
      return [
        { value: periodPresets.LAST_HOUR, label: this.$t('Última hora') },
        { value: periodPresets.TODAY, label: this.$t('Hoje') },
        { value: periodPresets.YESTERDAY, label: this.$t('Ontem') },
        { value: periodPresets.LAST_7_DAYS, label: this.$t('Últimos 7 dias') },
        { value: periodPresets.CUSTOM, label: this.$t('Personalizado') },
      ]
    },
    lastUpdatedLabel() {
      if (!this.lastUpdatedAt) return this.$t('Ainda não atualizado')
      // `now` só existe para reavaliar o texto relativo a cada tique do timer.
      const at = moment(this.lastUpdatedAt).format('HH:mm:ss')
      const ago = moment(this.lastUpdatedAt).from(this.now)
      return `${this.$t('Atualizado em')} ${at} (${ago})`
    },
  },

  mounted() {
    this.refresh()
    this.refreshTimer = setInterval(this.onTick, AUTO_REFRESH_MS)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },

  beforeDestroy() {
    clearInterval(this.refreshTimer)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },

  methods: {
    ...mapActions('pages/sale/autoDeliveryMonitor', ['fetchMonitor', 'setFilters']),

    async refresh() {
      if (this.fetching) return
      if (!this.isPeriodValid()) return
      try {
        await this.fetchMonitor()
        this.now = Date.now()
        this.refreshAlertFilter()
      } catch (error) {
        this.showError({ error })
      }
    },

    isPeriodValid() {
      if (this.periodPreset !== periodPresets.CUSTOM) return true
      const { startDate, endDate } = this.rangeDate || {}
      if (!startDate || !endDate) return false
      if (moment(endDate).diff(moment(startDate), 'days') >= MAX_PERIOD_DAYS) {
        this.showWarning({
          message: `${this.$t('O período máximo do painel, em dias, é')} ${MAX_PERIOD_DAYS}.`,
        })
        return false
      }
      return true
    },

    onTick() {
      this.now = Date.now()
      if (document.hidden) return
      this.refresh()
    },

    onVisibilityChange() {
      this.autoRefreshPaused = document.hidden
      if (!document.hidden) this.refresh()
    },

    selectAlert(alert) {
      if (!alert) {
        this.alertFilter = null
        return
      }
      const found = (this.health?.alerts || []).find(item => item.alert === alert)
      this.alertFilter = found ? { alert, saleIds: found.saleIds || [] } : null
      this.activeTab = 0
    },

    // Mantém o filtro de alerta com as vendas afetadas da leitura mais recente.
    refreshAlertFilter() {
      if (!this.alertFilter) return
      const found = (this.health?.alerts || []).find(item => item.alert === this.alertFilter.alert)
      this.alertFilter = found?.count ? { ...this.alertFilter, saleIds: found.saleIds || [] } : null
    },

    selectStore(storeId) {
      this.storeIds = [storeId]
      this.activeTab = 0
    },

    openSale(saleId) {
      this.openedSaleId = saleId
    },
  },
}
</script>
